import { useState, useEffect } from 'react';
import { useAuth } from '../../../contexts/AuthContext';
import { supabase } from '../../../lib/supabase';

interface GiftModalProps {
  onClose: () => void;
  onSuccess: () => void;
}

export default function GiftModal({ onClose, onSuccess }: GiftModalProps) {
  const { user } = useAuth();
  const [members, setMembers] = useState<any[]>([]);
  const [selectedMember, setSelectedMember] = useState('');
  const [amount, setAmount] = useState('');
  const [message, setMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [balance, setBalance] = useState(0);

  useEffect(() => {
    loadMembers();
    loadBalance();
  }, []);

  const loadMembers = async () => {
    try {
      const { data, error } = await supabase
        .from('profiles')
        .select('id, full_name, church_name')
        .neq('id', user?.id)
        .order('full_name');

      if (error) throw error;
      setMembers(data || []);
    } catch (error) {
      console.error('회원 목록 로드 실패:', error);
    }
  };

  const loadBalance = async () => {
    if (!user) return;

    try {
      const { data, error } = await supabase
        .from('talent_points')
        .select('balance')
        .eq('user_id', user.id)
        .single();

      if (error) throw error;
      setBalance(data?.balance || 0);
    } catch (error) {
      console.error('잔액 조회 실패:', error);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!user || !selectedMember || !amount) {
      alert('모든 필드를 입력해주세요.');
      return;
    }

    const giftAmount = parseInt(amount);

    if (giftAmount < 100) {
      alert('최소 100 달란트부터 선물할 수 있습니다.');
      return;
    }

    if (giftAmount > balance) {
      alert('보유 달란트가 부족합니다.');
      return;
    }

    setLoading(true);

    try {
      // 보내는 사람 차감
      const { data: senderData, error: senderError } = await supabase
        .from('talent_points')
        .select('balance, total_spent')
        .eq('user_id', user.id)
        .single();

      if (senderError) throw senderError;

      await supabase
        .from('talent_points')
        .update({
          balance: senderData.balance - giftAmount,
          total_spent: senderData.total_spent + giftAmount,
        })
        .eq('user_id', user.id);

      // 받는 사람 증가
      const { data: receiverData, error: receiverError } = await supabase
        .from('talent_points')
        .select('balance, total_earned')
        .eq('user_id', selectedMember)
        .single();

      if (receiverError) throw receiverError;

      await supabase
        .from('talent_points')
        .update({
          balance: receiverData.balance + giftAmount,
          total_earned: receiverData.total_earned + giftAmount,
        })
        .eq('user_id', selectedMember);

      // 거래 내역 생성 (보내는 사람)
      await supabase
        .from('point_transactions')
        .insert({
          user_id: user.id,
          amount: -giftAmount,
          type: 'spend',
          description: `달란트 선물 (받는 사람: ${members.find(m => m.id === selectedMember)?.full_name})${message ? ` - ${message}` : ''}`,
        });

      // 거래 내역 생성 (받는 사람)
      await supabase
        .from('point_transactions')
        .insert({
          user_id: selectedMember,
          amount: giftAmount,
          type: 'reward',
          description: `달란트 선물 받음${message ? ` - ${message}` : ''}`,
        });

      alert('달란트 선물이 완료되었습니다!');
      onSuccess();
      onClose();
    } catch (error) {
      console.error('선물 실패:', error);
      alert('선물 처리 중 오류가 발생했습니다.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl max-w-md w-full p-6">
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-2xl font-bold text-gray-900">달란트 선물하기</h3>
          <button
            onClick={onClose}
            className="w-8 h-8 flex items-center justify-center text-gray-400 hover:text-gray-600 cursor-pointer"
          >
            <i className="ri-close-line text-2xl"></i>
          </button>
        </div>

        <div className="bg-teal-50 rounded-lg p-4 mb-6">
          <p className="text-sm text-teal-800">
            보유 달란트: <span className="font-bold text-lg">{balance.toLocaleString()}</span>
          </p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              받는 사람
            </label>
            <select
              value={selectedMember}
              onChange={(e) => setSelectedMember(e.target.value)}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
              required
            >
              <option value="">회원 선택</option>
              {members.map((member) => (
                <option key={member.id} value={member.id}>
                  {member.full_name} {member.church_name ? `(${member.church_name})` : ''}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              선물 금액
            </label>
            <input
              type="number"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              placeholder="최소 100 달란트"
              min="100"
              max={balance}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              메시지 (선택)
            </label>
            <textarea
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              placeholder="선물과 함께 전할 메시지를 입력하세요"
              maxLength={200}
              rows={3}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent resize-none"
            />
            <p className="text-xs text-gray-500 mt-1">{message.length}/200</p>
          </div>

          <div className="flex gap-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-6 py-3 border border-gray-300 text-gray-700 font-semibold rounded-lg hover:bg-gray-50 transition-colors cursor-pointer whitespace-nowrap"
            >
              취소
            </button>
            <button
              type="submit"
              disabled={loading}
              className="flex-1 px-6 py-3 bg-teal-600 text-white font-semibold rounded-lg hover:bg-teal-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer whitespace-nowrap"
            >
              {loading ? '처리 중...' : '선물하기'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
